import { applyI18n, t } from '../i18n';
import { formatBytes } from '../i18n/formatBytes';
import { parseHar } from '../files/harParse';

export async function renderHarPreview(content: HTMLElement, blob: Blob): Promise<boolean> {
  try {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const capture = parseHar(bytes);
    renderHar(content, capture);
    return true;
  } catch {
    return false;
  }
}

function renderHar(
  content: HTMLElement,
  capture: ReturnType<typeof parseHar>,
): void {
  const wrap = document.createElement('div');
  wrap.className = 'file-preview-har';

  if (capture.creator) {
    const summary = document.createElement('p');
    summary.className = 'file-preview-har-summary';
    summary.textContent = capture.creator;
    wrap.append(summary);
  }

  if (capture.truncated) {
    const truncated = document.createElement('p');
    truncated.className = 'muted file-preview-har-truncated';
    truncated.textContent = t('files.preview.har.truncated', { count: capture.entries.length });
    wrap.append(truncated);
  }

  const split = document.createElement('div');
  split.className = 'file-preview-har-split';

  const tableWrap = document.createElement('div');
  tableWrap.className = 'file-preview-table-wrap file-preview-har-table-wrap';
  const table = document.createElement('table');
  table.className = 'data-table file-preview-table file-preview-har-table';
  table.innerHTML = `<thead><tr>
    <th data-i18n="files.preview.har.col.time"></th>
    <th data-i18n="files.preview.har.col.method"></th>
    <th data-i18n="files.preview.har.col.url"></th>
    <th data-i18n="files.preview.har.col.status"></th>
    <th data-i18n="files.preview.har.col.duration"></th>
    <th data-i18n="files.preview.har.col.size"></th>
  </tr></thead>`;
  const tbody = document.createElement('tbody');
  table.append(tbody);

  const detail = document.createElement('div');
  detail.className = 'file-preview-har-detail';
  const fields = document.createElement('dl');
  fields.className = 'file-preview-har-fields';
  detail.append(fields);

  let selected: (typeof capture.entries)[number] | null = capture.entries[0] ?? null;

  const renderRow = (entry: (typeof capture.entries)[number], selectedIndex: number | null) => {
    const tr = document.createElement('tr');
    if (entry.index === selectedIndex) tr.setAttribute('aria-selected', 'true');
    tr.innerHTML = `
      <td>${escapeHtml(entry.startedDateTime)}</td>
      <td>${escapeHtml(entry.method)}</td>
      <td class="file-preview-har-url">${escapeHtml(entry.url)}</td>
      <td>${entry.status ?? '—'}</td>
      <td>${entry.durationMs != null ? `${entry.durationMs.toFixed(1)} ms` : '—'}</td>
      <td>${entry.sizeBytes != null ? formatBytes(entry.sizeBytes) : '—'}</td>
    `;
    tr.addEventListener('click', () => {
      selected = entry;
      tbody.querySelectorAll('tr').forEach((row) => row.removeAttribute('aria-selected'));
      tr.setAttribute('aria-selected', 'true');
      renderDetail(fields, entry);
    });
    return tr;
  };

  for (const entry of capture.entries) {
    tbody.append(renderRow(entry, selected?.index ?? null));
  }
  if (selected) renderDetail(fields, selected);

  tableWrap.append(table);
  split.append(tableWrap, detail);
  wrap.append(split);
  content.append(wrap);
  applyI18n(wrap);
}

function renderDetail(fields: HTMLElement, entry: ReturnType<typeof parseHar>['entries'][number]): void {
  fields.replaceChildren();
  const append = (labelKey: string, value: string) => {
    const dt = document.createElement('dt');
    dt.textContent = t(labelKey);
    const dd = document.createElement('dd');
    dd.textContent = value;
    fields.append(dt, dd);
  };

  append('files.preview.har.detail.url', entry.url);
  append('files.preview.har.detail.method', entry.method);
  if (entry.status != null) append('files.preview.har.detail.status', String(entry.status));
  if (entry.requestBody) append('files.preview.har.detail.requestBody', entry.requestBody);
  if (entry.responseBody) append('files.preview.har.detail.responseBody', entry.responseBody);

  const requestHeaders = Object.entries(entry.requestHeaders).map(([k, v]) => `${k}: ${v}`).join('\n');
  if (requestHeaders) append('files.preview.har.detail.requestHeaders', requestHeaders);
  const responseHeaders = Object.entries(entry.responseHeaders).map(([k, v]) => `${k}: ${v}`).join('\n');
  if (responseHeaders) append('files.preview.har.detail.responseHeaders', responseHeaders);
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

